import { useState, useEffect, useMemo } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { clientesApi } from '../../api/notasVenta'
import api from '../../api/client'
import { fmtFecha } from '../../utils/fecha'
import { colors, font, text } from '../../design/tokens'
import { Seccion, Cargando, ErrorMsg, Vacio, fmtMoney, Titulo, TabsSegmento, Card, Buscador } from './comunes'
import useDebounce from '../../hooks/useDebounce'

// Cuenta corriente del cliente en el celular. Misma fuente que la pantalla
// grande (/clientes/historia): snapshot FTP de Presea vía
// GET /clientes/{id}/historia-presea. Mismo criterio de saldo: arranca en el
// saldo anterior al rango y los REMITO planos no lo mueven (mercadería, no deuda).

const PRESETS = [
  { key: '90',    label: '90 días' },
  { key: '30',    label: '30 días' },
  { key: 'actual', label: 'Año actual' },
  { key: 'todos', label: 'Todo' },
]

function rangoPreset(key) {
  const hoy = new Date()
  const f = (d) => fmtFecha(d)
  if (key === 'todos') return { d: '01/01/2000', h: f(hoy) }
  if (key === '30' || key === '90') {
    const d = new Date(); d.setDate(d.getDate() - Number(key))
    return { d: f(d), h: f(hoy) }
  }
  return { d: `01/01/${hoy.getFullYear()}`, h: f(hoy) }
}

export default function MovilClienteHistoria() {
  const { id } = useParams()
  const location = useLocation()
  const [cliente, setCliente] = useState(location.state?.cliente || null)
  const [preset, setPreset] = useState('90')
  const [resp, setResp] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (cliente) return
    clientesApi.obtener(id).then(({ data }) => setCliente(data)).catch(() => {})
  }, [id, cliente])

  useEffect(() => {
    const { d, h } = rangoPreset(preset)
    setLoading(true)
    clientesApi.historiaPresea(id, d, h)
      .then(({ data }) => { setResp(data); setError(null) })
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar la cuenta corriente.'))
      .finally(() => setLoading(false))
  }, [id, preset])

  // Saldo acumulado fila a fila — MISMO criterio que computarSaldos del
  // escritorio: los REMITO planos (mercadería entregada, no facturada) se
  // muestran pero NO mueven el saldo ni los totales.
  const { filas, saldoFinal, totalDebe, totalHaber } = useMemo(() => {
    let saldo = Number(resp?.saldo_anterior || 0)
    let debe = 0
    let haber = 0
    const filas = (resp?.filas || []).map((f) => {
      if (f.es_remito) return { ...f, saldo: null }
      saldo += (f.debe || 0) - (f.haber || 0)
      debe += f.debe || 0
      haber += f.haber || 0
      return { ...f, saldo }
    })
    return { filas, saldoFinal: saldo, totalDebe: debe, totalHaber: haber }
  }, [resp])

  return (
    <div>
      <Titulo>Cuenta corriente</Titulo>
      <BotonPortal clienteId={id} />

      {cliente && (
        <div style={{ margin: '-4px 0 10px' }}>
          <div style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 15, color: colors.text }}>{cliente.nombre}</div>
          <div style={{ fontSize: text.sec, color: colors.textSec, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
            {cliente.codigo_presea ? `Nº ${cliente.codigo_presea}` : ''}{cliente.cuit ? ` · ${cliente.cuit}` : ''}
          </div>
        </div>
      )}

      <TabsSegmento options={PRESETS.map((p) => ({ k: p.key, label: p.label }))} value={preset} onChange={setPreset} />

      {loading ? <Cargando /> : error ? <ErrorMsg>{error}</ErrorMsg> : (
        <>
          {/* Resumen del período */}
          <Seccion style={{ marginTop: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
              <div>
                <div style={kpiLbl}>Debe</div>
                <div style={kpiVal}>{fmtMoney(totalDebe)}</div>
              </div>
              <div>
                <div style={kpiLbl}>Haber</div>
                <div style={kpiVal}>{fmtMoney(totalHaber)}</div>
              </div>
              <div>
                <div style={kpiLbl}>Saldo</div>
                <div style={{ ...kpiVal, color: saldoFinal > 0.009 ? colors.err : colors.ok }}>{fmtMoney(saldoFinal)}</div>
              </div>
            </div>
            {Number(resp?.saldo_anterior || 0) !== 0 && (
              <p style={{ margin: '8px 0 0', fontSize: 11.5, color: colors.textMeta, textAlign: 'center', fontFamily: font.sans }}>
                Saldo anterior al período: {fmtMoney(resp.saldo_anterior)}
              </p>
            )}
          </Seccion>

          {/* Movimientos */}
          {filas.length === 0 ? (
            <Vacio>Sin movimientos en el período.</Vacio>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
              <div style={{ fontSize: text.sec, color: colors.textMeta, padding: '0 2px' }}>
                {filas.length} movimiento{filas.length === 1 ? '' : 's'} (el más nuevo abajo)
              </div>
              {filas.map((f, i) => (
                <div key={i} style={filaCard}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 13, color: colors.text, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.concepto}
                    </span>
                    <span style={monoMeta}>{f.fecha}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 3 }}>
                    {f.nro_comprobante && <span style={monoMeta}>Nº {f.nro_comprobante}</span>}
                    <span style={{ flex: 1 }} />
                    {f.debe > 0 && <span style={{ ...monoVal, color: f.es_remito ? colors.textMeta : colors.err }}>+{fmtMoney(f.debe)}</span>}
                    {f.haber > 0 && <span style={{ ...monoVal, color: f.es_remito ? colors.textMeta : colors.ok }}>−{fmtMoney(f.haber)}</span>}
                    {f.es_remito
                      ? <span style={{ fontSize: 11, color: colors.textMeta, fontFamily: font.sans, whiteSpace: 'nowrap' }}>remito · no suma</span>
                      : <span style={{ ...monoVal, color: colors.textSec }}>= {fmtMoney(f.saldo)}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

const kpiLbl = { fontSize: text.meta, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textSec, fontFamily: font.sans }
const kpiVal = { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 13.5, fontWeight: 700, color: colors.text, marginTop: 2 }
const filaCard = { background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 12, padding: '9px 12px' }
const monoMeta = { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 11.5, color: colors.textMeta, whiteSpace: 'nowrap', flexShrink: 0 }
const monoVal = { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap' }


// Abre el portal del cliente (la vista que ve ÉL: envíos y comprobantes).
function BotonPortal({ clienteId }) {
  const [ocupado, setOcupado] = useState(false)
  const abrir = async () => {
    setOcupado(true)
    try {
      const { data } = await api.get(`/clientes/${clienteId}/link-portal`)
      if (data.url) window.open(data.url, '_blank')
    } catch { /* sin ficha o sin permiso: nada que abrir */ } finally { setOcupado(false) }
  }
  return (
    <button
      onClick={abrir} disabled={ocupado}
      style={{ margin: '6px 0 10px', padding: '9px 14px', borderRadius: 10, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.textSec, fontSize: 13, fontWeight: 600, fontFamily: font.sans, cursor: 'pointer' }}
    >
      {ocupado ? 'Abriendo…' : '🔗 Ver como lo ve el cliente (portal)'}
    </button>
  )
}


// /m/historia — elegir el cliente cuya cuenta corriente querés ver (el botón
// "Historia de clientes" de Clientes, igual que Deudores tiene el suyo).
export function MovilHistoriaBuscar() {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const qBuscar = useDebounce(q, 350)
  const [resultados, setResultados] = useState([])
  const [buscando, setBuscando] = useState(false)

  useEffect(() => {
    if (qBuscar.trim().length < 2) { setResultados([]); setBuscando(false); return }
    let vivo = true
    setBuscando(true)
    clientesApi.buscar(qBuscar)
      .then(({ data }) => { if (vivo) setResultados(data.data || data || []) })
      .catch(() => { if (vivo) setResultados([]) })
      .finally(() => { if (vivo) setBuscando(false) })
    return () => { vivo = false }
  }, [qBuscar])

  return (
    <div>
      <Titulo>Historia de clientes</Titulo>
      <Buscador value={q} onChange={setQ} placeholder="Buscar cliente por nombre, CUIT o código…" />
      {buscando ? <Cargando /> : q.trim().length >= 2 && resultados.length === 0 ? (
        <Vacio>Sin resultados.</Vacio>
      ) : q.trim().length < 2 ? (
        <p style={{ fontSize: 12.5, color: colors.textMeta, textAlign: 'center', padding: 26, fontFamily: font.sans }}>
          Escribí al menos 2 letras.<br />Tocá un cliente para ver su cuenta corriente.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {resultados.slice(0, 25).map((c) => (
            <Card key={c.id} onClick={() => navigate(`/m/clientes/${c.id}/historia`, { state: { cliente: c } })}>
              <div style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 14.5, color: colors.text }}>{c.nombre}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginTop: 3 }}>
                {c.codigo_presea
                  ? <span style={monoMeta}>Nº {c.codigo_presea}</span>
                  : <span style={{ fontSize: text.sec, color: colors.textSec, fontFamily: font.sans }}>sin código</span>}
                {c.cuit && <span style={monoMeta}>{c.cuit}</span>}
                <span style={{ flex: 1 }} />
                {c.localidad && <span style={{ fontSize: text.sec, color: colors.textMeta, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.localidad}</span>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
